// THE ONE EMBEDDED CHECKOUT, AND WHETHER IT IS ON.
//
// EMBEDDED_CHECKOUT (docs/DECISIONS.md, 2026-10-06; G-255) replaces the two
// pay paths on the booking screen (the hosted Stripe Checkout redirect for a
// card, and the legacy Payment Request button for a wallet) with one Stripe
// Elements group mounted right under the postcode, house number and mobile:
// the Express Checkout Element with Apple Pay first, then the Payment Element
// for a card. Robin: "personally we just want one checkout view.." and
// "apple pay on desktop too..".
//
// Why Elements and not Stripe's Embedded Checkout: embedded Checkout runs in
// an iframe on Stripe's domain, so Apple Pay inside it "requires Safari 17 or
// later" (docs.stripe.com/testing/wallets?ui=embedded-page), which means no
// Apple Pay in desktop Chrome, the exact flow Robin uses. The Express
// Checkout Element can show Apple Pay in Chrome, Edge and Firefox through the
// iPhone QR flow when `paymentMethods.applePay` is 'always'
// (docs.stripe.com/elements/express-checkout-element#supported-browsers);
// it shows on Apple devices only (WALLETS_BY_PLATFORM below).
//
// The server writes the switch into the `gwmc:checkout` meta tag only while
// it is on (app/views/layouts/gwmc.html.haml); no tag is off, which is what
// production does until Robin flips it. Never a build-time constant.

import { colors, radius } from '../theme/tokens.js'
import { washRate } from './wallet-address.js'

const readMode = () => {
  if (typeof document === 'undefined') return null
  const tag = document.querySelector('meta[name="gwmc:checkout"]')
  return tag ? tag.getAttribute('content') : null
}

export const embeddedCheckout = () => readMode() === 'embedded'

// WALLET_ADDRESS_FROM_SHEET (18 Oct 2026): the wallet sheet gives the car's
// address and the phone, and a wallet payer types nothing. Its own switch,
// off by default (ships dark); the layout writes `gwmc:wallet-address` only
// while it is on, or for a browser that opened a page with ?wallet_address=1.
// Off: the page as it was. Only with the embedded checkout.
export const walletAddressFromSheet = () =>
  embeddedCheckout() && document.querySelector('meta[name="gwmc:wallet-address"]')?.getAttribute('content') === 'sheet'

// The rails, in the same list the server gives the SetupIntent
// (User::Stripe::EMBEDDED_PAYMENT_METHOD_TYPES): a deferred-intent Elements
// group refuses an intent whose types differ. Apple Pay and Google Pay are
// cards.
export const PAYMENT_METHOD_TYPES = ['card', 'link']

// ONE Express Checkout button, then "Pay by card" (12 Oct 2026). Robin:
// "also 3 pay buttons is a bit overwhelming imo what do you suggest?" and
// "any mac user users apple pay, and pc user its unclear as they could well
// have an iphone, and android google pay perhaps but I'd question between
// google pay and onlink?" Approved: "yea agree on wallet, and we always have
// or pay by card right?" order_paid.method by payer OS since 18 Sep:
// apple_pay iOS 5, Mac 1; link Windows 3, Mac 2; card 3; google_pay 0. So
// Link stays: it is the desktop wallet.
//
// The platform is decided once, before the element is made, and the element
// is made once with only that wallet allowed. Robin, 12 Oct: "I want less
// rather than more logic in booking form as complex javascript will put us a
// risk of browser incompatibilities?" If that wallet is not there, nothing
// shows and "Pay by card" carries the payment (the card form opens itself).
//
// Apple Pay is 'always' ("apple pay on desktop too..", 6 Oct): Stripe shows
// it in non-Safari desktop browsers only with 'always' (iPhone QR flow), and
// order 51818 (12 Oct, macOS Chrome) was paid that way. Values per
// docs.stripe.com/js/elements_object/create_express_checkout_element:
// applePay/googlePay 'auto' | 'always' | 'never', link 'auto' | 'never'.
export const WALLETS_BY_PLATFORM = {
  apple: { applePay: 'always', googlePay: 'never', link: 'never' },
  android: { applePay: 'never', googlePay: 'auto', link: 'never' },
  other: { applePay: 'never', googlePay: 'never', link: 'auto' }
}

// 'apple' (iOS, iPadOS, macOS), 'android' or 'other'. iPadOS Safari says
// "Macintosh"; userAgentData (Chromium only) is the better answer when there.
export const platformOf = (nav = typeof navigator === 'undefined' ? {} : navigator) => {
  const p = `${nav.userAgentData?.platform || ''} ${nav.userAgent || ''}`
  return /Android/i.test(p) ? 'android' : /iPhone|iPad|iPod|Mac|iOS/i.test(p) ? 'apple' : 'other'
}

// fromSheet: WALLET_ADDRESS_FROM_SHEET. Off, the sheet asks the email only,
// as before 18 Oct.
export const expressOptions = (platform = platformOf(), fromSheet = false) => ({
  paymentMethods: WALLETS_BY_PLATFORM[platform],
  buttonType: { applePay: 'book', googlePay: 'book' },
  buttonTheme: { applePay: 'black', googlePay: 'black' },
  buttonHeight: 54,
  layout: { maxColumns: 1, overflow: 'never' },
  // The payer's email is the one order mail goes to (W23(a), G-251).
  emailRequired: true,
  // The wallet sheet is where the car is (18 Oct 2026, amending 5 Oct's
  // "never a shipping address: the car is where the customer typed"). Robin:
  // "yea I think we need to be able to get the details from apple or google
  // pay", then "fewest boxes and fields is what Im thinking..". So the sheet
  // asks for the address (UK only) and the phone, and a wallet payer types
  // nothing on the page (lib/wallet-address.js). shippingAddressRequired
  // needs shippingRates at create, click or shippingaddresschange: one free
  // "Wash at this address" to start; the address check may add the
  // Congestion Charge one.
  phoneNumberRequired: fromSheet,
  ...(fromSheet && { shippingAddressRequired: true, allowedShippingCountries: ['GB'], shippingRates: [washRate()] })
})

// The card form. Link stays off in it (11 Oct 2026): inside the card form it
// added Link's save-and-sign-up block ("First and last name", "By providing
// phone number and email, you agree to create an account..."), extra fields
// on the card path. Link stays as an Express Checkout button.
//
// The platform's own wallet is ON in it since 17 Oct 2026: Apple Pay 'auto'
// on Apple devices, Google Pay 'auto' on Android, nothing elsewhere. Every
// iPhone payer who pressed a pay button after 11 Oct opened the card form
// (5 of 5), and that form had lost the Apple Pay the hosted Stripe page used
// to offer. Robin, 17 Oct: "I dont want to downgrade apple pay as I think its
// how everyone wants to pay.. on iphone etc.." So opening the card form never
// takes Apple Pay away (docs/DECISIONS.md, 17 Oct 2026). Payment Element
// values: applePay/googlePay 'auto' | 'never'.
export const paymentElementOptions = (platform = platformOf()) => ({
  layout: 'tabs',
  wallets: {
    applePay: platform === 'apple' ? 'auto' : 'never',
    googlePay: platform === 'android' ? 'auto' : 'never',
    link: 'never'
  }
})

// The Payment Element tab that is a wallet ('apple_pay' | 'google_pay'), or
// null for the card. Takes the element's change event value.type.
export const cardFormWallet = type => (type === 'apple_pay' || type === 'google_pay' ? type : null)

// Stripe Elements' appearance from DESIGN.md §1 tokens (theme/tokens.js is
// the JS mirror that exists for exactly this). Poppins is our own woff2, so
// it is handed to Stripe's iframe as an absolute URL.
//
// `faces` is [{ weight, path }] and the caller supplies it, which looks like
// indirection and is not: the paths carry Propshaft's content digest and only
// an importer can know them (step-checkout/one-checkout.js imports the three
// files; esbuild turns each import into a digested URL, esbuild.config.mjs).
// This module is unit tested under plain node, which cannot import a .woff2,
// so the import cannot live here. The digest is the point: a path with one
// would otherwise be the last asset URL on the site that a one year cache
// could serve stale.
export const elementsOptions = (origin = '', faces = []) => ({
  mode: 'setup',
  currency: 'gbp',
  paymentMethodTypes: PAYMENT_METHOD_TYPES,
  appearance: {
    theme: 'stripe',
    variables: {
      colorPrimary: colors.accent,
      colorText: colors.ink,
      colorTextSecondary: colors.mute,
      colorBackground: colors.paper,
      colorDanger: colors.error,
      fontFamily: "Poppins, 'Helvetica Neue', Arial, sans-serif",
      fontSizeBase: '16px',
      borderRadius: `${radius.control}px`
    },
    rules: {
      '.Input': { borderColor: colors.line, boxShadow: 'none' },
      '.Input:focus': { borderColor: colors.accent }
    }
  },
  fonts: origin
    ? faces.map(face => ({ family: 'Poppins', weight: face.weight, src: `url(${origin}${face.path})` }))
    : []
})

// What the Apple Pay sheet says. A deferred payment request asks Apple for a
// merchant token (MPAN) rather than a device token, which keeps working when
// the customer changes phone, and shows the amount and the day it is taken
// instead of "amount pending". Charged after the booking, or once the wash is
// done under CHARGE_ON_COMPLETION; either way not before the wash day.
export const applePayRequest = ({ amountPence, washDate, origin = '' }) => {
  if (!amountPence || amountPence <= 0) return undefined
  const date = washDate instanceof Date && !isNaN(washDate) ? washDate : new Date()
  return {
    deferredPaymentRequest: {
      paymentDescription: 'GoWashMyCar wash',
      // Apple wants a page about managing the payment. There is no account
      // page; the FAQ answers "When do I pay?" and how to cancel.
      managementURL: `${origin || 'https://www.gowashmycar.com'}/faqs`,
      deferredBilling: {
        amount: amountPence,
        label: 'GoWashMyCar',
        deferredPaymentDate: date
      }
    }
  }
}

// The card section (email + Payment Element + its Book button). Robin, 7 Oct:
// "I would expand the card section once clicked the pay by card button,
// otherwise they go for apple or google or online..". So on every device it
// waits, collapsed, behind "Pay by card" under the wallet row, and opens when
// that is tapped. It starts open only when there is nothing else to pay with:
// the Express Checkout Element reported no wallet at all, or Stripe did not
// load (so the error under the card form shows).
const noWallets = wallets => Array.isArray(wallets) && wallets.length === 0

export const cardSectionOpen = ({ wallets = null, tapped = false, loadFailed = false } = {}) =>
  !!(tapped || loadFailed || noWallets(wallets))

// A tap on "Or pay by card" (replay 01a14e90, 18 Oct 2026, Mac Chrome with a
// 705 px window): the form opened below the fold, the payer tapped again
// 0.6 s later and shut it, then double-clicked. So a tap on a section that
// opened under CARD_REOPEN_GUARD_MS ago keeps it open ('show': bring it into
// view); only a later tap shuts it. The tap that opens it on an incomplete
// booking also says what is missing (showMissing), as a refused wallet tap
// does, instead of opening somewhere nobody is looking.
export const CARD_REOPEN_GUARD_MS = 1500
export const cardTriggerTap = ({ open = false, openedAt = 0, now = 0, complete = true } = {}) => {
  if (!open) return { action: 'open', showMissing: !complete }
  if (now - openedAt < CARD_REOPEN_GUARD_MS) return { action: 'show', showMissing: false }
  return { action: 'close', showMissing: false }
}

// "Pay by card" shows while there is a wallet row to choose instead (or while
// the Express Checkout Element is still deciding: wallets null).
export const showCardTrigger = ({ wallets = null, loadFailed = false } = {}) =>
  !loadFailed && !noWallets(wallets)

// The express button the payer used, in our analytics taxonomy (ANALYTICS.md
// §2: wallet_payment_started `method`).
// Takes the event's expressPaymentType or an availablePaymentMethods key.
export const walletMethodOf = type =>
  ({ apple_pay: 'apple_pay', applePay: 'apple_pay', google_pay: 'google_pay', googlePay: 'google_pay', link: 'link' })[type] || 'wallet'
